/**
 * Create Sector Component
 *
 * UI for creating a new sector room under a command room.
 */

import { useState } from 'react'
import type { GeoLocation } from '@/lib/types'
import { GEOFENCE_RADIUS_OPTIONS } from '@/lib/types'

interface CreateSectorProps {
  commandRoomId: string // Used for sector creation context
  commandRoomName: string
  currentLocation?: GeoLocation
  onCreateSector: (params: {
    name: string
    center: GeoLocation
    radiusMeters: number
  }) => void
  onCancel: () => void
}

// commandRoomId is passed for context but the actual creation happens via onCreateSector callback

export function CreateSector({
  commandRoomId: _commandRoomId,
  commandRoomName,
  currentLocation,
  onCreateSector,
  onCancel,
}: CreateSectorProps) {
  const [name, setName] = useState('')
  const [latitude, setLatitude] = useState(currentLocation?.latitude.toString() || '')
  const [longitude, setLongitude] = useState(currentLocation?.longitude.toString() || '')
  const [radiusMeters, setRadiusMeters] = useState(250)
  const [useCurrentLocation, setUseCurrentLocation] = useState(!!currentLocation)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)

    if (!name.trim()) {
      alert('Please enter a sector name')
      return
    }

    if (isNaN(lat) || isNaN(lng)) {
      alert('Please enter valid coordinates')
      return
    }

    onCreateSector({
      name: name.trim(),
      center: { latitude: lat, longitude: lng },
      radiusMeters,
    })
  }

  const handleUseCurrentLocation = () => {
    if (currentLocation) {
      setLatitude(currentLocation.latitude.toString())
      setLongitude(currentLocation.longitude.toString())
      setUseCurrentLocation(true)
    } else {
      // Try to get current location
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude.toString())
          setLongitude(position.coords.longitude.toString())
          setUseCurrentLocation(true)
        },
        (error) => {
          console.error('Failed to get location:', error)
          alert('Could not get current location. Please enter coordinates manually.')
        }
      )
    }
  }

  return (
    <div className="bg-gradient-to-b from-[#152028] to-[#0a1014] rounded-2xl overflow-hidden max-h-[90vh] flex flex-col border border-teal/30 shadow-[0_0_60px_rgba(27,127,143,0.2)]">
      {/* Fixed Header - Teal Theme */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-teal/30 via-transparent to-teal/30 border-b border-teal/30 flex-shrink-0">
        <h2 className="text-lg font-bold text-white flex items-center gap-2 tracking-wide">
          <span className="text-xl">➕</span> Create New Sector
        </h2>
        <button
          onClick={onCancel}
          className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all border border-white/10 hover:border-white/30"
        >
          ✕
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="p-4 space-y-4 overflow-y-auto flex-1">
        <p className="text-sm text-white/60">
          Creating sector under: <span className="text-primary font-medium">{commandRoomName}</span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" id="create-sector-form">
        {/* Sector name */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">
            Sector Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., North Street Team"
            className="w-full bg-secondary/50 text-white placeholder-white/40 rounded-lg p-2
              text-sm border border-white/10 focus:border-primary focus:outline-none"
          />
        </div>

        {/* Location */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-white/80">
              Sector Center
            </label>
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              className="text-xs text-primary hover:text-primary/80 transition-colors"
            >
              📍 Use current location
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={latitude}
              onChange={(e) => {
                setLatitude(e.target.value)
                setUseCurrentLocation(false)
              }}
              placeholder="Latitude"
              className="bg-secondary/50 text-white placeholder-white/40 rounded-lg p-2
                text-sm border border-white/10 focus:border-primary focus:outline-none"
            />
            <input
              type="text"
              value={longitude}
              onChange={(e) => {
                setLongitude(e.target.value)
                setUseCurrentLocation(false)
              }}
              placeholder="Longitude"
              className="bg-secondary/50 text-white placeholder-white/40 rounded-lg p-2
                text-sm border border-white/10 focus:border-primary focus:outline-none"
            />
          </div>
          {useCurrentLocation && currentLocation && (
            <p className="text-xs text-green-400 mt-1">
              ✓ Using current location
            </p>
          )}
        </div>

        {/* Radius */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">
            Geofence Radius
          </label>
          <div className="flex flex-wrap gap-2">
            {GEOFENCE_RADIUS_OPTIONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRadiusMeters(r)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                  ${radiusMeters === r
                    ? 'bg-primary text-white shadow-hard-sm'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
              >
                {r}m
              </button>
            ))}
          </div>
        </div>

        </form>
      </div>

      {/* Fixed Footer */}
      <div className="flex gap-2 p-4 border-t border-teal/20 flex-shrink-0 bg-black/30">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 bg-white/10 text-white/80 rounded-xl font-bold uppercase tracking-wider
            hover:bg-white/20 transition-all border border-white/20"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="create-sector-form"
          className="flex-1 py-3 bg-gradient-to-r from-teal to-teal/80 text-white rounded-xl font-bold uppercase tracking-wider
            hover:from-teal/90 hover:to-teal/70 transition-all shadow-[0_0_20px_rgba(27,127,143,0.3)]"
        >
          Create Sector
        </button>
      </div>
    </div>
  )
}

export default CreateSector
